// pending orders
import React, { useContext, useState, useMemo } from "react";
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, TableSortLabel, TextField, Tooltip, IconButton, ButtonGroup, Typography, Box } from "@mui/material";
import { ThemeContext } from "../../../../context/ThemeContext";
import { closeOrder } from "../../../../api/request/dashboard/dashboard";
import CloseIcon from "@mui/icons-material/Close";
import '../../../../App.scss'
import { Search } from "@mui/icons-material";
import usePendingOrderCell from "../../../tablecells/PendingOrderCell";
import toast from 'react-hot-toast';
import Checkbox from '@mui/material/Checkbox';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { TableVirtuoso } from "react-virtuoso";
// import { useSymbolPrices } from "../../../../context/WebSocketContext";
import { deleteOrdersData } from '../../../../store/Slices/orderPositionSlice'
import NoDataTable from "../../../table/NoDataTable";

export default function Orders() {
    const { darkMode } = useContext(ThemeContext);
    const accountId = localStorage.getItem('accountID');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [showSearchBar, setShowSearchBar] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);
    const [hoveredCell, setHoveredCell] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const { t } = useTranslation()
    const PendingOrderCell = usePendingOrderCell()
    const decimalPlaces = useSelector((state) => state.user.decimalPlacesForAmountFormatting);
    // const symbols = useSymbolPrices()
    const { symbols } = useSelector(state => state.watchlist)
    const orders = useSelector((state) => state.orderPosition.orders, shallowEqual);
    const orderHistory = orders || [];
    const dispatch = useDispatch()

    const handleMouseEnter = (rowIndex, cellIndex) => {
        setHoveredRow(rowIndex);
        setHoveredCell(cellIndex);
    };

    const handleMouseLeave = () => {
        setHoveredRow(null);
        setHoveredCell(null);
    };

    const enrichedOrders = orders.length ? orders?.map(order => {
        const matchingSymbol = Object.values(symbols).find(symbol => Number(symbol.id) === Number(order.symbolId));
        return {
            ...order,
            currentPrice: matchingSymbol?.bid || "N/A"
        };
    }) : [];

    const sortTradeData = [...enrichedOrders].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const valueA = a[sortConfig.key];
        const valueB = b[sortConfig.key];

        if (typeof valueA === 'number' && typeof valueB === 'number') {
            return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
        }
        if (typeof valueA === 'string' && typeof valueB === 'string') {
            return sortConfig.direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        }
        return 0;
    });

    const filteredData = sortTradeData.filter((row) =>
        PendingOrderCell.some((col) => row[col.dataIndex]?.toString().toLowerCase().includes(searchQuery.toLowerCase()))
    );
    // console.log("Final Data to Render:", filteredData);

    const allSelected = selectedRows.length === filteredData.length && filteredData.length > 0;
    const handleRowSelect = (rowId) => {
        setSelectedRows((prev) =>
            prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
        );
    };

    const handleSelectAll = () => {
        setSelectedRows(allSelected ? [] : filteredData.map(row => row.id));
    };

    const handleCloseClick = async (rowIndex = null) => {
        const ordersToClose = rowIndex !== null
            ? [orderHistory[rowIndex].id]
            : selectedRows;

        if (ordersToClose.length === 0) {
            toast.error("No orders selected");
            return;
        }

        let successCount = 0;
        let failureCount = 0;


        for (const orderId of ordersToClose) {
            try {
                const selectedRow = orderHistory.find(row => row.id === orderId);
                if (!selectedRow) continue;

                const closeOrderPayload = { accountId: Number(accountId), id: selectedRow.id };
                const closeResponse = await closeOrder(closeOrderPayload);

                // if (closeResponse.status === 200) {
                //     successCount++;
                //     dispatch(deleteOrdersData(orderId));
                // }
                if (closeResponse.status === 200) {
                    successCount++;
                    dispatch(deleteOrdersData(orderId));

                    const { id, symbolId, side, pl } = selectedRow;
                    const symbolName = symbols[symbolId]?.name || "Unknown Symbol";
                    const price = side === 1 ? symbols[symbolId]?.bid : symbols[symbolId]?.ask;
                    const tradeSide = side === 1 ? "Buy" : "Sell";

                    toast.success(`Closed Trade #${id} (${symbolName}) at ${price} | ${tradeSide}`);
                }
                else {
                    failureCount++;
                    toast.error(` Failed to close Trade #${selectedRow.id} (${symbols[selectedRow.symbolId]?.name || "Unknown"})`);

                }
            } catch (error) {
                console.error("Error closing order:", error);
                toast.error(` Failed to close Trade`);

                failureCount++;
            }
        }

        // if (successCount > 0) {
        // toast.success(`${successCount} Order(s) closed successfully`);
        // }

        if (failureCount > 0) {
            toast.error(`${failureCount} trade(s) failed to close`);
        }
        setSelectedRows([]);
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handleToggleSearchBar = () => {
        setShowSearchBar((prev) => {
            if (prev) setSearchQuery("");
            return !prev;
        });
    }
    const Row = useMemo(() => {
        return (index, row) => {
            return (
                <>
                    <TableCell padding="checkbox" sx={{ border: "none", padding: "5px 5px", backgroundColor: darkMode ? "#151924" : "#fff", }}>
                        <Checkbox checked={selectedRows.includes(row.id)} onChange={() => handleRowSelect(row.id)} sx={{ width: 24, height: 24, "& .MuiSvgIcon-root": { fontSize: 12 }, }} />
                    </TableCell>

                    {PendingOrderCell.map((col, colIndex) => {
                        const isLastVisibleColumn = colIndex === PendingOrderCell.length - 1;

                        return (
                            <TableCell key={col.dataIndex} onMouseEnter={() => handleMouseEnter(index, colIndex)} onMouseLeave={handleMouseLeave} sx={{ fontSize: "0.8rem", border: "none", padding: "5px 5px", backgroundColor: darkMode ? "#151924" : "#fff", position: "relative", overflow: "hidden", }}>
                                {col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]}

                                {isLastVisibleColumn && hoveredRow === index && (
                                    <Tooltip title={t("close")} arrow>
                                        <IconButton size="small" sx={{ position: "absolute", top: "50%", right: "5px", transform: "translateY(-50%)", backgroundColor: darkMode ? "#151924" : "#fff", }} onClick={() => handleCloseClick(index)}>
                                            <CloseIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </TableCell>
                        );
                    })}
                </>
            );
        };
    }, [hoveredRow]);



    const fixedHeaderContent = () => {
        return <TableRow sx={{ borderTop: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}` }}>
            <TableCell padding="checkbox" sx={{ border: "none", padding: "5px 5px", backgroundColor: darkMode ? "#151924" : "#fff", }}>
                <ButtonGroup variant="outlined" sx={{ display: "flex", alignItems: "center", gap: 1, border: 'none' }}>
                    <Tooltip title={t('selectall')}>
                        <Checkbox checked={allSelected} onChange={handleSelectAll} indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length} sx={{ width: 24, height: 24, "& .MuiSvgIcon-root": { fontSize: 14 } }} />
                    </Tooltip>
                    <Tooltip title={t('delete')}>
                        <span>
                            <IconButton onClick={() => handleCloseClick()} sx={{ color: "red", width: 24, height: 24 }} disabled={selectedRows.length === 0}><DeleteOutlinedIcon sx={{ fontSize: 14 }} /></IconButton>
                        </span>
                    </Tooltip>
                </ButtonGroup>
            </TableCell>
            {PendingOrderCell.map(
                (col, index) => (
                    <TableCell key={index} sx={{ fontSize: "0.85rem", fontWeight: 200, p: 1, backgroundColor: darkMode ? "var(--background-color)" : "var(--white)", cursor: "pointer", border: "none", borderRight: `1px solid ${darkMode ? "#444" : "var(--borderLight)"}`, }} onClick={() => handleSort(col.dataIndex)}>
                        <TableSortLabel active={sortConfig.key === col.dataIndex} direction={sortConfig.direction}>
                            {col.title}
                        </TableSortLabel>
                    </TableCell>
                )
            )}
        </TableRow>
    }
    const VirtuosoTableComponents = {
        Table: (props) => (
            <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
        ),
        TableHead: React.forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
        TableRow,
        TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    };

    return (
        <>
            {filteredData.length === 0 ? (
                <NoDataTable columns={PendingOrderCell} data={filteredData} noDataMessage={t('noData')} />
            ) : (
                <Paper style={{ width: "100%", height: 400, position: 'relative' }}>
                    <div style={{ position: "absolute", top: 10, right: 16, zIndex: 1000, display: "flex", alignItems: "center", transition: "width 0.4s ease-in-out", }}>
                        {!showSearchBar && (
                            <IconButton onClick={handleToggleSearchBar} sx={{ border: darkMode ? "1px solid #202933" : "1px solid #d5d5d5", borderRadius: "50%", transition: "opacity 0.3s ease-in-out", }}>
                                <Search sx={{ fontSize: 18 }} />
                            </IconButton>
                        )}
                        {showSearchBar && (
                            <Paper sx={{ display: "flex", alignItems: "center", borderRadius: 50, width: "250px", boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)", marginLeft: "8px", padding: "2px 8px", transition: "all 0.3s ease-in-out", backgroundColor: darkMode ? "#202933" : "#d5d5d5" }}>
                                <TextField size="small" variant="outlined" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ "& fieldset": { border: "none" }, flex: 1, }} />
                                <IconButton onClick={handleToggleSearchBar}>
                                    <CloseIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Paper>
                        )}
                    </div>
                    <Box sx={{ backgroundColor: darkMode ? "#151924" : "#fff", height: '100%' }}>
                        <TableVirtuoso data={filteredData} itemContent={Row} fixedHeaderContent={fixedHeaderContent} components={VirtuosoTableComponents}> </TableVirtuoso>
                    </Box>
                </Paper>
            )}
        </>
    );
}
